import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { Box, Typography } from '@mui/material';
import { useTimeline } from '../TimelineContext';
import { useChart } from '../../../../components/chart';
import { friendlyNameConverter } from '../../../../services/localizationService';

export default function SensorChartWidget({ recording, sensorKey, events = [] }) {
  const { currentTime, seek, window: timeWindow } = useTimeline();

  const chartData = useMemo(() => {
    if (!recording?.data?.[sensorKey]) return null;
    const points = recording.data[sensorKey];
    return {
      labels: points.map((dp) => dp.time),
      series: [
        {
          type: 'line',
          name: sensorKey,
          fill: 'solid',
          data: points.map((dp) => dp.value),
        },
      ],
    };
  }, [recording, sensorKey]);

  const sensorName = friendlyNameConverter(sensorKey);

  const eventAnnotations = useMemo(() =>
    events.map((evt) => ({
      x: new Date(evt.startTime).getTime(),
      x2: new Date(evt.endTime).getTime(),
      fillColor: '#d50100',
      label: { text: evt.label },
    })),
    [events]
  );

  const chartOptions = useChart({
    plotOptions: { bar: { columnWidth: '16%' } },
    fill: { type: ['solid'] },
    labels: chartData?.labels || [],
    xaxis: {
      type: 'datetime',
      min: timeWindow?.start,
      max: timeWindow?.end,
      labels: { datetimeUTC: false },
    },
    annotations: {
      xaxis: [
        ...eventAnnotations,
        {
          x: currentTime,
          borderColor: '#775DD0',
          label: {
            borderColor: '#775DD0',
            style: { color: '#fff', background: '#775DD0' },
            text: 'Current Time',
          },
        },
      ],
    },
    chart: {
      toolbar: {
        show: true,
        tools: { download: true, selection: true, pan: true, zoom: true },
      },
      events: {
        markerClick: (_, chartContext, opts) => {
          const index = opts.dataPointIndex;
          if (chartData?.labels && index >= 0) {
            seek(chartData.labels[index]);
          }
        },
      },
    },
    tooltip: {
      custom: ({ series, dataPointIndex, w }) => {
        const value = series[0]?.[dataPointIndex] ?? 'N/A';
        const color = w.globals.colors[0];
        return `
          <div style="padding: 8px; border-radius: 9px;">
            <div style="display: flex; align-items: center;">
              <span style="background-color: ${color}; border-radius: 50%; width: 12px; height: 12px; margin-right: 8px;"></span>
              <span>${sensorName}:&nbsp;&nbsp;<strong>${value}</strong></span>
            </div>
          </div>
        `;
      },
    },
  });

  if (!chartData) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Typography color="text.secondary" variant="body2">
          No data for {sensorName}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1, pb: 0, width: '100%', height: '100%' }} dir="ltr">
      <ReactApexChart type="line" series={chartData.series} options={chartOptions} height="100%" />
    </Box>
  );
}
